import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react'
import type * as React from 'react'
import { type ChevronProps, DayButton, type DayButtonProps, DayPicker } from 'react-day-picker'
import { cn } from '../../lib/utils'
import { buttonVariants } from './button'

export type CalendarProps = React.ComponentProps<typeof DayPicker>
function CalendarChevron({ className, orientation = 'left', size = 16 }: ChevronProps) {
  if (orientation === 'left') {
    return <ChevronLeft className={className} size={size} />
  }
  if (orientation === 'up') {
    return <ChevronUp className={className} size={size} />
  }
  if (orientation === 'down') {
    return <ChevronDown className={className} size={size} />
  }
  return <ChevronRight className={className} size={size} />
}

function CalendarDayButton(props: DayButtonProps) {
  const testId = props.day.isoDate + (props.day.outside ? '+1' : '')

  return (
    <div data-testid={testId}>
      <DayButton {...props} />
    </div>
  )
}

function Calendar({ className, classNames, showOutsideDays = true, navLayout = 'around', ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      navLayout={navLayout}
      className={cn('p-3', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'relative space-y-4',
        month_caption: 'relative mx-8 flex items-center justify-center pt-1',
        caption_label: 'text-sm font-medium',
        nav: 'space-x-1 flex items-center',
        button_previous: cn(
          buttonVariants({ variant: 'outline' }),
          'absolute top-0 left-1 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        button_next: cn(
          buttonVariants({ variant: 'outline' }),
          'absolute top-0 right-1 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        month_grid: 'w-full border-collapse space-y-1',
        weekdays: 'flex',
        weekday: 'text-secondary rounded-md w-8 font-normal text-[0.8rem]',
        week: 'flex w-full mt-2',
        day: cn(
          'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 aria-selected:bg-sub-surface-bg [&.day-outside[aria-selected]]:bg-sub-surface-bg/50',
          props.mode === 'range'
            ? '[&.day-range-end]:rounded-r-md [&.day-range-start]:rounded-l-md first:aria-selected:rounded-l-md last:aria-selected:rounded-r-md'
            : 'aria-selected:rounded-md',
        ),
        day_button: cn(buttonVariants({ variant: 'ghost' }), 'h-8 w-8 p-0 font-normal'),
        range_start: 'day-range-start',
        range_end: 'day-range-end',
        selected:
          '[&>div>button]:bg-primary-accent [&>div>button]:text-header [&>div>button:hover]:bg-primary [&>div>button:hover]:text-header-inverse [&>div>button:focus]:bg-primary-accent [&>div>button:focus]:text-sub-surface-bg',
        today: '[&>div>button]:bg-sub-surface-bg [&>div>button]:text-header',
        outside:
          'day-outside [&>div>button]:text-secondary [&[aria-selected]>div>button]:bg-sub-surface-bg/50 [&[aria-selected]>div>button]:text-secondary',
        disabled: '[&>div>button]:text-secondary [&>div>button]:opacity-50 [&[aria-selected]>div>button]:opacity-100',
        range_middle: '[&>div>button]:bg-sub-surface-bg [&>div>button]:text-header',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: CalendarChevron,
        DayButton: CalendarDayButton,
      }}
      {...props}
    />
  )
}
Calendar.displayName = 'Calendar'

export { Calendar }
